"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const vue_1 = require("vue");
const vue_2 = require("vue");
const _hoisted_1 = { class: "tw" };
const _hoisted_2 = ["for"];
const _hoisted_3 = {
    key: 0,
    "aria-hidden": "true"
};
const _hoisted_4 = ["id", "aria-describedby", "autocomplete", "autocapitalize", "autocorrect", "disabled", "inputmode", "max", "maxlength", "min", "name", "pattern", "placeholder", "readonly", "required", "spellcheck", "type", "value", "data-currency"];
const _hoisted_5 = {
    key: 0,
    class: "pointer-events-none col-start-1 row-start-2 justify-self-start pl-4"
};
const vue_3 = require("vue");
const SharedButtonOnlyIcon_vue_1 = __importDefault(require("./SharedButtonOnlyIcon.vue"));
const SvgIcon_vue_1 = __importDefault(require("./SvgIcon/SvgIcon.vue"));
const use_input_validation_1 = __importDefault(require("../composables/use-input-validation"));
const EMAIL_PATTERN = "^[\\w.+\\-]+@(?!xn--|\\.)[\\w.\\-]+(\\.[A-Za-z]{2,})$";
exports.default = (0, vue_1.defineComponent)({
    __name: 'SharedTextfield',
    props: {
        autocomplete: {},
        autocapitalize: {},
        autocorrect: {},
        currency: {},
        dataTestName: { default: "" },
        disabled: { type: Boolean },
        displayName: {},
        forcedErrorMessage: {},
        hintPermanent: { type: Boolean },
        inputmode: {},
        label: {},
        max: {},
        maxlength: {},
        min: {},
        modelValue: {},
        name: {},
        pattern: {},
        patternErrorMessage: {},
        placeholder: {},
        readonly: { type: Boolean },
        required: { type: Boolean },
        requiredErrorMessage: {},
        requiredFlag: { type: Boolean, default: true },
        spellcheck: { type: Boolean },
        type: { default: "text" },
        validateOnChange: { type: Boolean },
        validateOnBlur: { type: Boolean },
        value: { default: "" }
    },
    emits: ["update:modelValue", "focus", "input", "change", "blur", "form-submit"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const emit = __emit;
        const props = __props;
        const inputRef = (0, vue_3.ref)(null);
        __expose({ inputRef });
        const useVModel = (0, vue_3.computed)(() => typeof props.modelValue !== "undefined");
        const inputValue = useVModel.value
            ? (0, vue_3.computed)({
                get() {
                    return String(props.modelValue ?? "");
                },
                set(val) {
                    emit("update:modelValue", val);
                },
            })
            : (0, vue_3.ref)(props.value);
        (0, vue_3.watch)(() => props.value, (val) => {
            inputValue.value = val;
        });
        /** Validation, Hint, Accessibility */
        const UNIQUE_ID = `input-${Math.random().toString(36).slice(2, 9)}`;
        const validationId = `validation-${UNIQUE_ID}`;
        const { isValidationVisible, errorMessage, handleInput, handleChange, handleBlur } = (0, use_input_validation_1.default)(inputRef, props.displayName || props.name || "Feld", {
            patternErrorMessage: props.patternErrorMessage,
        }, (0, vue_3.toRef)(props, "forcedErrorMessage"), props.requiredErrorMessage);
        const hintId = `hint-${UNIQUE_ID}`;
        const isHintVisible = (0, vue_3.computed)(() => !!(0, vue_3.useSlots)().default && (props.hintPermanent || !isValidationVisible.value));
        const ariaDescribedBy = (0, vue_3.computed)(() => {
            if (isHintVisible.value || isValidationVisible.value) {
                return `${isHintVisible.value ? hintId : ""} ${isValidationVisible.value ? validationId : ""}`.trim();
            }
            return undefined;
        });
        const dataTestErrorName = (0, vue_3.computed)(() => (props.dataTestName ? `${props.dataTestName}-error` : ""));
        /** Type Password */
        const localType = (0, vue_3.ref)(props.type);
        const isPasswordVisible = (0, vue_3.ref)(false);
        const togglePassword = () => {
            isPasswordVisible.value = !isPasswordVisible.value;
            localType.value = isPasswordVisible.value ? "text" : "password";
        };
        /** Email Pattern */
        // Hybris-RegEx für E-Mail, s. DefaultConfigValues.java: ^[\\w.+\\-]+@(?!xn--)[\\w.\\-]+(\\.[A-Za-z]{2,})$
        // TUB-18168: ergänzt durch "." nach dem "@"
        const localPattern = (0, vue_3.ref)(props.pattern);
        const _setLocalPattern = () => {
            localPattern.value = props.type === "email" && !props.pattern ? EMAIL_PATTERN : props.pattern;
        };
        (0, vue_3.onMounted)(() => {
            _setLocalPattern();
        });
        (0, vue_3.onUpdated)(() => {
            _setLocalPattern();
        });
        /** Events */
        const onFocus = (e) => {
            emit("focus", e, { errorMessage: errorMessage.value });
        };
        const onInput = (e) => {
            const val = e.target.value;
            if (useVModel.value) {
                emit("update:modelValue", val);
            }
            else {
                inputValue.value = val;
            }
            handleInput(); // Validation
            emit("input", e, { errorMessage: errorMessage.value });
        };
        const onChange = (e) => {
            if (!props.validateOnBlur)
                handleChange(props.validateOnChange); // Validation
            emit("change", e, { errorMessage: errorMessage.value });
        };
        const onBlur = (e) => {
            if (props.validateOnBlur)
                handleBlur(props.validateOnBlur); // Validation
            emit("blur", e, { errorMessage: errorMessage.value });
        };
        /** Form Submit */
        const formRef = (0, vue_3.inject)("formRef", (0, vue_3.ref)(null));
        if (formRef.value) {
            formRef.value.addEventListener("submit", handleSubmit);
        }
        else {
            (0, vue_3.watch)(formRef, () => {
                formRef.value?.addEventListener("submit", handleSubmit);
            });
        }
        function handleSubmit(e) {
            emit("form-submit", e, { errorMessage: errorMessage.value });
        }
        return (_ctx, _cache) => {
            return ((0, vue_2.openBlock)(), (0, vue_2.createElementBlock)("div", _hoisted_1, [
                (0, vue_2.createElementVNode)("div", {
                    class: (0, vue_2.normalizeClass)(["shared-textfield relative grid items-center", { 'is-invalid': (0, vue_2.unref)(isValidationVisible), 'is-readonly': _ctx.readonly, 'is-disabled': _ctx.disabled }])
                }, [
                    (0, vue_2.createElementVNode)("label", {
                        class: "shared-textfield-label ocm-body1 relative z-10 mx-3 -mb-3 w-fit bg-white px-1",
                        for: _ctx.name
                    }, [
                        (0, vue_2.createTextVNode)((0, vue_2.toDisplayString)(_ctx.label), 1),
                        (_ctx.required && _ctx.requiredFlag)
                            ? ((0, vue_2.openBlock)(), (0, vue_2.createElementBlock)("span", _hoisted_3, "*"))
                            : (0, vue_2.createCommentVNode)("", true)
                    ], 8, _hoisted_2),
                    (0, vue_2.createElementVNode)("input", (0, vue_2.normalizeProps)({
                        id: _ctx.name,
                        ref_key: "inputRef",
                        ref: inputRef,
                        "aria-describedby": ariaDescribedBy.value,
                        autocomplete: _ctx.autocomplete,
                        autocapitalize: _ctx.autocapitalize,
                        autocorrect: _ctx.autocorrect,
                        class: ["shared-textfield-input col-start-1 row-start-2 h-14 rounded text-base", { 'has-currency': _ctx.currency }],
                        [_ctx.dataTestName || ""]: '',
                        disabled: _ctx.disabled,
                        inputmode: _ctx.inputmode,
                        max: _ctx.max,
                        maxlength: _ctx.maxlength,
                        min: _ctx.min,
                        name: _ctx.name,
                        pattern: localPattern.value,
                        placeholder: _ctx.placeholder,
                        readonly: _ctx.readonly,
                        required: _ctx.required,
                        spellcheck: props.type === 'email' ? 'false' : props.spellcheck,
                        title: "",
                        type: localType.value,
                        value: (0, vue_2.unref)(inputValue),
                        "data-currency": _ctx.currency,
                        onFocus: onFocus,
                        onInput: onInput,
                        onChange: onChange,
                        onBlur: onBlur
                    }), null, 16, _hoisted_4),
                    (_ctx.currency)
                        ? ((0, vue_2.openBlock)(), (0, vue_2.createElementBlock)("div", _hoisted_5, (0, vue_2.toDisplayString)(_ctx.currency), 1))
                        : (0, vue_2.createCommentVNode)("", true),
                    (_ctx.type === 'password')
                        ? ((0, vue_2.openBlock)(), (0, vue_2.createBlock)(SharedButtonOnlyIcon_vue_1.default, {
                            key: 1,
                            class: "col-start-1 row-start-2 mr-1.5 size-10 justify-self-end focus-visible:outline-current",
                            "icon-name": isPasswordVisible.value ? 'visibility-off' : 'visibility',
                            "icon-size": "5",
                            disabled: _ctx.disabled,
                            onClick: togglePassword
                        }, null, 8, ["icon-name", "disabled"]))
                        : (0, vue_2.createCommentVNode)("", true)
                ], 2),
                (isHintVisible.value)
                    ? ((0, vue_2.openBlock)(), (0, vue_2.createElementBlock)("div", {
                        key: 0,
                        id: hintId,
                        class: "mt-1 text-xs text-gray-80"
                    }, [
                        (0, vue_2.renderSlot)(_ctx.$slots, "default")
                    ]))
                    : (0, vue_2.createCommentVNode)("", true),
                ((0, vue_2.unref)(isValidationVisible))
                    ? ((0, vue_2.openBlock)(), (0, vue_2.createElementBlock)("div", (0, vue_2.normalizeProps)({
                        key: 1,
                        id: validationId,
                        class: "on-show-slide-in mt-1 grid grid-cols-[min-content,1fr] gap-x-1 text-xs text-error",
                        [dataTestErrorName.value || ""]: '',
                        "aria-live": "polite"
                    }), [
                        (0, vue_2.createVNode)(SvgIcon_vue_1.default, {
                            class: "size-4",
                            "icon-name": "error",
                            title: "Fehler:"
                        }),
                        (0, vue_2.createTextVNode)(" " + (0, vue_2.toDisplayString)((0, vue_2.unref)(errorMessage)), 1)
                    ], 16))
                    : (0, vue_2.createCommentVNode)("", true)
            ]));
        };
    }
});
